import { lazy } from 'react'
import { AccountTypeGuard } from '@/components/auth/AccountTypeGuard'
import { SubscriptionFeatureGuard } from '@/components/auth/SubscriptionFeatureGuard'
import { OrgRouteGuard } from '@/components/organization/OrgRouteGuard'
import { Navigate, Outlet, useParams } from 'react-router-dom'

import {
  AccountType,
  IntegrationType,
  isIntegrationVisible,
} from '@scrambl/shared'

import { ProtectedLayout } from '../components'
import { AppRouteObject } from '../types'

// Lazy-loaded admin pages — keeps initial bundle out of non-admin sessions
const AdminIntegrationsPage = lazy(() => import('@/pages/admin/integrations'))
const QuickBooksIntegrationPage = lazy(
  () => import('@/pages/admin/integrations/quickbooks'),
)
const QuickBooksIntegrationErrorPage = lazy(
  () => import('@/pages/admin/integrations/quickbooks-error'),
)
const QuickBooksIntegrationSuccessPage = lazy(
  () => import('@/pages/admin/integrations/quickbooks-success'),
)
const AdminMembersPage = lazy(() => import('@/pages/admin/members'))
const AdminSettingsPage = lazy(() => import('@/pages/admin/settings'))
const AdminSubscriptionPage = lazy(() => import('@/pages/admin/subscription'))
const AdminSubscriptionPlansPage = lazy(
  () => import('@/pages/admin/subscription/plans'),
)
const AdminSubscriptionSetupSuccessPage = lazy(
  () => import('@/pages/admin/subscription/setup-success'),
)
const AdminSubscriptionSuccessPage = lazy(
  () => import('@/pages/admin/subscription/success'),
)

const AdminAuditLogsPage = lazy(() => import('@/pages/admin/logs'))
const AdminArchivePage = lazy(() => import('@/pages/admin/archive'))

export function LegacyAdminTemplatesRedirect({
  destination = 'statuses',
}: {
  destination?: 'statuses' | 'project-templates'
}) {
  const { orgSlug } = useParams()
  const target =
    destination === 'project-templates'
      ? `/${orgSlug}/projects/templates`
      : `/${orgSlug}/projects/settings?section=statuses`

  return <Navigate to={target} replace />
}

// Admin routes configuration
export const adminRoutes: AppRouteObject[] = [
  {
    path: '/:orgSlug/admin',
    element: <ProtectedLayout />,
    children: [
      {
        element: <OrgRouteGuard />,
        children: [
          // Default redirect to settings
          {
            index: true,
            element: <Navigate to="settings" replace />,
          },
          // Legacy admin dashboard route
          {
            path: 'dashboard',
            element: <Navigate to="../settings" replace />,
            title: 'Admin Settings',
          },
          // Admin members
          {
            path: 'members',
            element: <AdminMembersPage />,
            title: 'Members',
          },
          // Admin permissions
          {
            path: 'permissions',
            element: (
              <SubscriptionFeatureGuard featureKey="custom_permission_sets">
                <AdminMembersPage />
              </SubscriptionFeatureGuard>
            ),
            title: 'Permissions',
          },
          // Admin external organizations
          {
            path: 'external-organizations',
            element: <AdminMembersPage />,
            title: 'External Organizations',
          },
          // Admin settings
          {
            path: 'settings',
            element: <AdminSettingsPage />,
            title: 'Admin Settings',
          },
          // Status configuration now lives in Project Settings. Keep redirects
          // for old Help Center links and saved bookmarks, but do not render
          // the retired admin templates experience.
          {
            path: 'templates',
            children: [
              {
                index: true,
                element: <LegacyAdminTemplatesRedirect />,
                title: 'Project Settings',
              },
              {
                path: 'status-templates',
                element: <LegacyAdminTemplatesRedirect />,
                title: 'Project Settings',
              },
              {
                path: 'project-templates',
                element: (
                  <LegacyAdminTemplatesRedirect destination="project-templates" />
                ),
                title: 'Project Templates',
              },
            ],
          },
          // Admin integrations (requires subscription feature)
          {
            path: 'integrations',
            element: (
              <SubscriptionFeatureGuard featureKey="integrations">
                <AdminIntegrationsPage />
              </SubscriptionFeatureGuard>
            ),
            title: 'Integrations',
          },
          {
            path: 'integrations/quickbooks',
            element: isIntegrationVisible(IntegrationType.QUICKBOOKS) ? (
              <SubscriptionFeatureGuard featureKey="quickbooks_online">
                <QuickBooksIntegrationPage />
              </SubscriptionFeatureGuard>
            ) : (
              <Navigate to="../integrations" replace />
            ),
            title: 'QuickBooks Settings',
          },
          {
            path: 'integrations/quickbooks/success',
            element: isIntegrationVisible(IntegrationType.QUICKBOOKS) ? (
              <SubscriptionFeatureGuard featureKey="quickbooks_online">
                <QuickBooksIntegrationSuccessPage />
              </SubscriptionFeatureGuard>
            ) : (
              <Navigate to="../integrations" replace />
            ),
            title: 'QuickBooks Connected',
          },
          {
            path: 'integrations/quickbooks/error',
            element: isIntegrationVisible(IntegrationType.QUICKBOOKS) ? (
              <SubscriptionFeatureGuard featureKey="quickbooks_online">
                <QuickBooksIntegrationErrorPage />
              </SubscriptionFeatureGuard>
            ) : (
              <Navigate to="../integrations" replace />
            ),
            title: 'QuickBooks Error',
          },
          {
            path: 'integrations/mcp',
            element: (
              <Navigate to="../../../user-settings#integrations" replace />
            ),
            title: 'MCP Integration',
          },
          // Legacy /admin/time/* → /timesheets/* (grace period: ≥ 2026-10-21)
          {
            path: 'time',
            children: [
              {
                index: true,
                element: <Navigate to="../../timesheets" replace />,
              },
              {
                path: 'pending',
                element: <Navigate to="../../../timesheets" replace />,
              },
              {
                path: 'team',
                element: <Navigate to="../../../timesheets" replace />,
              },
              {
                path: 'settings',
                element: <Navigate to="../../../timesheets/settings" replace />,
              },
            ],
          },
          // Admin subscription & billing — personal accounts are free by
          // construction (no SUBSCRIPTION flag row), so block the whole billing
          // surface by account type rather than relying on a feature flag.
          {
            path: 'subscription',
            element: (
              <AccountTypeGuard allowed={[AccountType.ORGANIZATION]}>
                <Outlet />
              </AccountTypeGuard>
            ),
            children: [
              {
                index: true,
                element: <AdminSubscriptionPage />,
                title: 'Subscription & Billing',
              },
              {
                path: 'usage',
                element: <AdminSubscriptionPage />,
                title: 'Subscription Usage',
              },
              {
                path: 'plans',
                element: <AdminSubscriptionPlansPage />,
                title: 'Subscription Plans',
              },
              {
                path: 'success',
                element: <AdminSubscriptionSuccessPage />,
                title: 'Payment Success',
              },
              {
                path: 'setup-success',
                element: <AdminSubscriptionSetupSuccessPage />,
                title: 'Setup Success',
              },
            ],
          },
          // Audit trail. OrgRouteGuard already blocks non-owner/admin members
          // from every /admin route, mirroring the controller's
          // @Access(ORGANIZATION, OWNER, ADMIN). The account-type guard is what
          // keeps personal accounts out: the subscription layer is inert for
          // them, so a subscription gate alone would leave this open.
          {
            path: 'logs',
            element: (
              <AccountTypeGuard allowed={[AccountType.ORGANIZATION]}>
                <SubscriptionFeatureGuard featureKey="audit_log">
                  <AdminAuditLogsPage />
                </SubscriptionFeatureGuard>
              </AccountTypeGuard>
            ),
            title: 'Audit Trail',
          },
          // Unified archive / restore. OrgRouteGuard already restricts every
          // /admin route to owner + admin, matching the controller's
          // @Access(ORGANIZATION, OWNER, ADMIN) — a cross-module view of
          // removed records is an admin surface, not a member one.
          //
          // Personal mode: HIDDEN. Per the account-separation contract new
          // surfaces ship work-only by default, and this one would additionally
          // need the personal vocabulary (Family / People / Address Book /
          // Vendors) before it read correctly in a personal space.
          {
            path: 'archive',
            element: (
              <AccountTypeGuard allowed={[AccountType.ORGANIZATION]}>
                <AdminArchivePage />
              </AccountTypeGuard>
            ),
            title: 'Archive',
          },
        ],
      },
    ],
  },
]
